import { useEffect, useState } from 'react';
import { IonCardContent } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { Text } from '../components/Text';
import { Card } from '../components/Card';
import { AnimatedCollapse } from '../components/AnimatedCollapse';
import { OnboardingProgressBar } from './ProgressBar';
import { IOnboardingTask } from './IOnBoardingTask';
import { TaskStepSection } from './TaskStepSection';
import { ChevronButton } from '../components/ChevronButton';
import { isAutomatedTests } from '../utils/isAutomatedTests';

export interface IOnboardingCardProps {
    task: IOnboardingTask;
    expanded?: boolean;
    showCompleted?: boolean;
}

export const OnboardingCard: React.FC<IOnboardingCardProps> = ({ task, expanded = false, showCompleted = false }) => {
    const [isOpen, setOpen] = useState(expanded);

    const mixpanel = useInstance(MixPanelEventEmitter);

    // expand first incomplete task
    const expandIndex = task.steps.findIndex(t => t.ready && !t.completed);
    const disabled = task.steps.every(s => !s.ready && !s.completed);
    const allComplete = task.steps.every(s => s.completed);

    useEffect(() => {
        // wait a bit so user sees it change on home page
        setTimeout(() => setOpen(expanded), isAutomatedTests() ? 0 : 500);
    }, [expanded]);

    const onToggle = () => {
        mixpanel.track('Onboarding Group Expansion Toggled', () => ({
            Name: task.heading,
            State: (isOpen ? 'closed' : 'open')
        }));

        setOpen(!isOpen);
    };

    return (
        (!allComplete || showCompleted) && (
            <Card dataProps={{'data-type': 'onboarding-card', 'data-expanded': String(isOpen)}}>
                <IonCardContent style={{cursor: isOpen ? undefined : 'pointer'}} onClick={isOpen ? undefined : onToggle}>

                    <div style={{display: 'flex', justifyContent: 'space-between', cursor: 'pointer'}} onClick={isOpen ? onToggle : undefined}>
                        <span style={{flexGrow: 1}}><Text data-type="heading" type="title" disabled={disabled}><b>{task.heading}</b></Text></span>
                        <ChevronButton direction={isOpen ? 'up' : 'down'} />
                    </div>

                    {task.text && (
                        <AnimatedCollapse collapsed={!isOpen}>
                            <div style={{marginTop: 10, marginBottom: 20}}>
                                {task.image && (
                                    <center><img src={task.image()} alt={`${task.heading}`} /></center>
                                )}
                                <Text type="detail" disabled={disabled}>{task.text}</Text>
                            </div>
                        </AnimatedCollapse>
                    )}

                    <div style={{marginTop: 10, marginBottom: 10}}>
                        <OnboardingProgressBar
                            steps={task.steps.filter(t => !t.hidden).length}
                            currentStep={task.steps.filter(t => t.completed).length}
                            disabled={disabled}
                        />
                    </div>

                    <AnimatedCollapse collapsed={!isOpen}>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20, marginBottom: 10}}>
                            {task.steps.map((step, i) => (
                                !step.hidden && (!step.completed || showCompleted) && (
                                    <TaskStepSection
                                        key={step.heading}
                                        step={step}
                                        expanded={i === expandIndex}
                                    />
                                )
                            ))}
                        </div>
                    </AnimatedCollapse>

                </IonCardContent>
            </Card>
        )
    );
};
